import {
  Text,
  ActionIcon,
  Autocomplete,
  Divider,
  Group,
  NumberInput,
  Select,
  Space,
  Stack,
  Box,
  Switch,
} from '@mantine/core';
import React, { FC, useContext, useState } from 'react';
import { IconDragDrop, IconDragDrop2, IconTrashX } from '@tabler/icons';
import { useDrag, useDrop } from 'react-dnd-latest';
import { useElementSize } from '@mantine/hooks';
import {
  BaseDocument,
  DiscountType,
  DragItem,
  PositionDragItem,
  PositionDto,
  PositionHandlers,
} from '../../../Service/DocumentT';
import { ProfileContext } from '../../../Service/Context/ProfileContext';
import { useJwtClaims } from '../../../Service/Context/AuthContext';
import { DataS } from '../../../Service/DataS';
import { TestS } from '../../../TestS';
import BookitupSegmentedControl from '../../../Atoms/BookitupSegmentedControl';
import { FormatS } from '../../../Service/FormatS';
import { EventRichInput, RichInput } from '../../../Organismns/tiptap/TiptapEditorInput';
import ResourcePositionAssignment from '../../../Organismns/Settings/Documents/Positions/ResourcePositionAssignment';
import BookingEvent from '../../../Types/Event';
import { useEventScope } from '../../../Service/hooks/useEventScope';
import DocumentBubbleMenu from '../../../Organismns/tiptap/toolbars/BubbleMenu';
import ConfigOption from '../../../Atoms/ConfigOption';
import { ResourceAddonContext } from '../../../Service/Context/ResourceAddonsContext';
import { DocumentService } from '../../../Service/helper/DocumentService';
import { TiptapS } from '../../../Service/TiptapS';
import LinkText from '../../../Atoms/LinkText';
import { ViolationContext } from '../../../Service/Context/ViolationContext';
import { LicenceS } from '../../../Service/LicenceS';
import { openAddProductModal } from './AddProductModal';
import { PositionPrev } from './PositionPreview';
import PositionAmountInput from './PositionAmountInput';
import { DocumentPositionActions } from './PositionActions';

interface Props {
  position: PositionDto;
  bookitupDocument: BaseDocument;
  handlers: PositionHandlers;
  products: PositionDto[];
  refreshDocument: () => void;
  event?: BookingEvent;
  previewVersion?: boolean;
}

const DocumentPosition: FC<Props> = ({
  position,
  bookitupDocument,
  refreshDocument,
  handlers,
  products,
  event,
  previewVersion = false,
}) => {
  const { profile } = useContext(ProfileContext);
  const { licence } = useJwtClaims();
  const [, setResourcePresent] = useState(false);
  const { isEventScope, eventId } = useEventScope();
  const { patchPosition, addPosition, deletePosition, movePosition, addProducts } = handlers;
  const { isActive } = useContext(ResourceAddonContext);

  const { taxType, documentType, positionGroups, currency, currencySymbol } = bookitupDocument;
  const {
    discount,
    id: positionId,
    label,
    subtext,
    price,
    tax,
    netPrice,
    priceUnit,
    netPriceUnit,
    unit,
    amount,
    optional,
    negotiable,
    min,
    max,
    autoSelect,
    type: positionKind,
  } = position;
  const { value: discountValue, type: discountType } = discount ?? { amount: 0, type: 'PERCENT' };
  const isPartialInvoice = documentType === 'PARTIAL_INVOICE';
  const isOffer = documentType === 'OFFER';
  const isSmallScaleBusiness = profile && profile.smallScaleBusiness;
  const { ref, width } = useElementSize();
  const { executeWithLicenceCheck } = useContext(ViolationContext);
  const hasFlowLicence = LicenceS.hasSuitableLicence('FLOW', licence);
  const isFreeTextPosition = positionKind === 'FREE_TEXT';
  const showPortalSettings = isOffer && !isFreeTextPosition;
  const showAutoSelectSetting = optional;
  const showFreeTextSetting = !isPartialInvoice && hasFlowLicence;
  const showPortalFlowSettings = showPortalSettings && hasFlowLicence;
  const showPositionSettings = showPortalSettings || showFreeTextSetting;

  const [{ isOver, canDrop }, drop] = useDrop(
    () => ({
      accept: DragItem.POSITION,
      canDrop: (item: PositionDragItem) => item.position.id !== position.id,
      collect: (monitor) => ({
        isOver: monitor.isOver(),
        canDrop: !!monitor.canDrop(),
      }),
      drop(item) {
        movePosition(item.position.id, position.id);
      },
    }),
    [position.id],
  );

  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DragItem.POSITION,
      item: { position },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
        handlerId: monitor.getHandlerId(),
      }),
    }),
    [position.id],
  );

  const addProduct = () =>
    executeWithLicenceCheck(LicenceS.Restrictions.PRODUCT_USAGE, () => {
      openAddProductModal({ products, onFinish: (p) => addProducts(p, positionId) });
    });

  const handleIntegerKeyDown = (keyEvent: React.KeyboardEvent<HTMLInputElement>) => {
    const navigationKeys = ['Backspace', 'Delete', 'Tab', 'Enter', 'Escape', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];

    if (
      keyEvent.metaKey ||
      keyEvent.ctrlKey ||
      keyEvent.altKey ||
      navigationKeys.includes(keyEvent.key) ||
      /^\d$/.test(keyEvent.key)
    ) {
      return;
    }

    keyEvent.preventDefault();
  };

  const handleIntegerPaste = (pasteEvent: React.ClipboardEvent<HTMLInputElement>) => {
    if (!/^\d+$/.test(pasteEvent.clipboardData.getData('text'))) {
      pasteEvent.preventDefault();
    }
  };

  const opacity = isDragging ? 0.4 : 1;

  if (previewVersion) {
    return (
      <>
        <Box
          pos="relative"
          sx={(theme) => ({
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[0],
            border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[1]}`,
            padding: 10,
            borderRadius: 6,
          })}
        >
          <PositionPrev
            document={bookitupDocument as any}
            taxType={taxType as any}
            favoritePosition={false}
            position={position}
            language={bookitupDocument.language}
          />
          <div style={{ position: 'absolute', top: 5, right: 5 }}>
            <DocumentPositionActions
              previewVersion
              position={position}
              positionHandlers={handlers}
              isPartialInvoice={isPartialInvoice}
              positionGroups={positionGroups}
            />
          </div>
        </Box>
        {documentType !== 'PARTIAL_INVOICE' && (
          <Group>
            <LinkText mt={-5} onClick={() => addPosition(positionId)}>
              + Leere Position einfügen
            </LinkText>
            <LinkText mt={-5} onClick={addProduct}>
              + Produkt auswählen
            </LinkText>
          </Group>
        )}
      </>
    );
  }

  return (
    <div ref={ref}>
      <Group noWrap ref={drop} spacing={5}>
        {!isPartialInvoice && (
          <div style={{ width: 30 }}>
            <ActionIcon ref={drag}>
              {canDrop && <IconDragDrop color="#1D5191" size={isOver ? 28 : 20} />}
              {!canDrop && <IconDragDrop2 size={20} style={{ cursor: 'grab' }} />}
            </ActionIcon>
          </div>
        )}
        {isPartialInvoice && <div style={{ width: 20 }} />}
        <Stack
          p="xs"
          spacing="xs"
          sx={(theme) => ({
            flexGrow: 1,
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[0],
            color: theme.colorScheme === 'dark' ? 'white' : 'black',
            borderRadius: 10,
            opacity,
          })}
        >
          <Group spacing="xs">
            <div
              style={{
                flex: isFreeTextPosition ? 1 : undefined,
                width: isFreeTextPosition ? undefined : Math.max(width - (isPartialInvoice ? 495 : 640), 160),
              }}
            >
              {isEventScope && (
                <EventRichInput
                  value={label}
                  patchField={(val) => patchPosition(positionId, { label: val })}
                  menu={<DocumentBubbleMenu />}
                />
              )}
              {!isEventScope && (
                <RichInput
                  value={label}
                  patchField={(val) => patchPosition(positionId, { label: val })}
                  menu={<DocumentBubbleMenu />}
                />
              )}
            </div>
            {!isFreeTextPosition && (
              <>
                <PositionAmountInput position={position} onChange={() => refreshDocument()} />
                <Autocomplete
                  defaultValue={unit ?? ''}
                  data={DataS.positionUnitSuggestions}
                  disabled={isPartialInvoice}
                  onBlur={(e) => patchPosition(positionId, { unit: e.currentTarget.value })}
                  sx={{ width: 60 }}
                />
                <NumberInput
                  hideControls
                  decimalSeparator=","
                  precision={2}
                  removeTrailingZeros
                  value={taxType === 'gross' ? priceUnit : netPriceUnit}
                  sx={{ width: 70 }}
                  onBlur={(e) => {
                    const tmp = FormatS.formatAsCurrency(e.currentTarget.value);
                    patchPosition(positionId, {
                      priceUnit: taxType === 'gross' ? tmp : DocumentService.calculateGrossPrice(tmp, tax, true),
                    });
                  }}
                  data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
                />
                {!isSmallScaleBusiness && (
                  <Select
                    value={tax.toString()}
                    data={DataS.getTaxRateOptions(currency, tax)}
                    sx={{ width: 55 }}
                    onChange={(val) => patchPosition(positionId, { tax: Number(val) })}
                    // eslint-disable-next-line react/jsx-no-useless-fragment
                    rightSection={<></>}
                    rightSectionWidth={0}
                  />
                )}
                {!isPartialInvoice && (
                  <>
                    <NumberInput
                      hideControls
                      min={0}
                      decimalSeparator=","
                      precision={Number.isInteger(discountValue) ? 0 : 2}
                      value={discountValue}
                      sx={{ width: 60 }}
                      onBlur={(e) =>
                        patchPosition(positionId, { discountValue: FormatS.formatAsCurrency(e.currentTarget.value) })
                      }
                      data-testid={TestS.Documents.Edit.Position.DISCOUNT_VALUE}
                    />
                    <BookitupSegmentedControl
                      value={discountType}
                      data={DataS.getDiscountUnitOptions(currencySymbol)}
                      onChange={(val: DiscountType) => val && patchPosition(positionId, { discountType: val })}
                      sx={{ marginLeft: -10 }}
                      testId={TestS.Documents.Edit.Position.DISCOUNT_TYPE}
                    />
                  </>
                )}
                <Text align="center" sx={{ width: 115 }}>
                  {FormatS.formatPriceWithCurrency(currency, taxType === 'net' ? netPrice : price)}
                </Text>
              </>
            )}
          </Group>
          <div style={{ width: width - 95 }}>
            {isEventScope && (
              <EventRichInput
                value={subtext}
                menu={<DocumentBubbleMenu />}
                patchField={(val) => patchPosition(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
              />
            )}
            {!isEventScope && (
              <RichInput
                value={subtext}
                menu={<DocumentBubbleMenu />}
                patchField={(val) => patchPosition(positionId, { subtext: TiptapS.convertParagraphsToBreaks(val) })}
              />
            )}
          </div>
          {!isFreeTextPosition && (
            <Group position="apart">
              {isActive && documentType !== 'PARTIAL_INVOICE' && (
                <ResourcePositionAssignment
                  position={position}
                  favoritePosition={false}
                  eventId={isEventScope ? Number(eventId) : undefined}
                  amount={amount}
                  setResourcePresent={setResourcePresent}
                />
              )}
              <div />
            </Group>
          )}
          {showPositionSettings && (
            <>
              <Divider label="Einstellungen" labelPosition="center" />
              <Stack px="xs" spacing="xs">
                {showFreeTextSetting && (
                  <ConfigOption
                    title="Freitext"
                    description="Zeigt nur die Beschreibung und Unterbeschreibung dieser Position an und blendet Preise, Mengen, Ressourcen und Portaloptionen aus."
                    rightSection={
                      <Switch
                        size="sm"
                        checked={isFreeTextPosition}
                        onChange={() =>
                          patchPosition(positionId, { type: isFreeTextPosition ? 'REGULAR' : 'FREE_TEXT' })
                        }
                      />
                    }
                  />
                )}
                {showPortalSettings && (
                  <ConfigOption
                    title="Optionale Position"
                    description="Kunden können diese Position im Portal auswählen oder abwählen."
                    rightSection={
                      <Switch
                        size="sm"
                        checked={optional}
                        onChange={() => patchPosition(positionId, { optional: !optional })}
                      />
                    }
                  />
                )}
                {showPortalSettings && showAutoSelectSetting && (
                  <ConfigOption
                    title="Option vorausgewählt"
                    description="Diese optionale Position ist im Portal standardmäßig ausgewählt."
                    rightSection={
                      <Switch
                        size="sm"
                        checked={autoSelect}
                        onChange={() => patchPosition(positionId, { autoSelect: !autoSelect })}
                      />
                    }
                  />
                )}
                {showPortalFlowSettings && (
                  <>
                    <ConfigOption
                      title="Kunde kann Menge wählen"
                      description="Kunden können die Menge im Portal selbst festlegen. Mindest- und Maximalmenge begrenzen den erlaubten Bereich."
                      rightSection={
                        <Switch
                          size="sm"
                          checked={negotiable}
                          onChange={() => patchPosition(positionId, { negotiable: !negotiable })}
                        />
                      }
                    />
                    {negotiable && (
                      <Group spacing={4} align="flex-end" position="right">
                        <NumberInput
                          hideControls
                          label={
                            <Text color="dimmed" size="xs">
                              Mindestmenge
                            </Text>
                          }
                          min={0}
                          precision={0}
                          placeholder="z.B. 1"
                          defaultValue={min}
                          sx={{ width: 100 }}
                          onKeyDown={handleIntegerKeyDown}
                          onPaste={handleIntegerPaste}
                          onBlur={(e) => patchPosition(positionId, { min: Number(e.currentTarget.value) || 0 })}
                        />
                        <NumberInput
                          hideControls
                          label={
                            <Text color="dimmed" size="xs">
                              Maximalmenge
                            </Text>
                          }
                          min={0}
                          precision={0}
                          placeholder="Kein Limit"
                          defaultValue={max === 0 ? undefined : max}
                          sx={{ width: 100 }}
                          onKeyDown={handleIntegerKeyDown}
                          onPaste={handleIntegerPaste}
                          onBlur={(e) => patchPosition(positionId, { max: Number(e.currentTarget.value) || 0 })}
                        />
                      </Group>
                    )}
                  </>
                )}
              </Stack>
            </>
          )}
        </Stack>
        <Stack justify="space-between">
          <DocumentPositionActions position={position} positionHandlers={handlers} positionGroups={positionGroups} />
          <Space h={40} />
          <ActionIcon onClick={() => deletePosition(positionId)}>
            <IconTrashX size={20} />
          </ActionIcon>
        </Stack>
      </Group>
      {documentType !== 'PARTIAL_INVOICE' && (
        <Group mt="md">
          <LinkText
            mt={-5}
            ml={30}
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={() => addPosition(positionId)}
          >
            + Leere Position einfügen
          </LinkText>
          <LinkText
            mt={-5}
            color="blue"
            variant="link"
            sx={{ cursor: 'pointer', display: 'inline-block' }}
            onClick={addProduct}
          >
            + Produkt auswählen
          </LinkText>
        </Group>
      )}
    </div>
  );
};

export default DocumentPosition;
